import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Pane,
  Card,
  Table,
  Button,
  majorScale,
  Spinner,
  Alert,
  Strong,
  Heading,
  Badge,
  Radio,
  Text,
  SearchInput,
  Pagination,
  IconButton,
  ChevronDownIcon,
  ChevronUpIcon,
} from 'evergreen-ui';
import { useCsvImport } from './hooks/useCsvImport';

const ITEMS_PER_PAGE = 10;

// Default editable fields if the API doesn't provide entity-specific fields
const DEFAULT_EDITABLE_FIELDS = [
  'title',
  'format',
  'edition',
  'subtitle',
  'creators',
  'description',
  'bisac',
  'retail_price',
  'publication_date',
  'ship_date',
  'carton_qty',
  'audience_details',
  'key_selling_points',
  'marketing',
];

// Default duplicate check fields (fallback)
const DEFAULT_DUPLICATE_CHECK_FIELDS = ['id']; // Use a sensible default if API fails

const DuplicateResolver = ({ importSession, onComplete, onError }) => {
  // Component state
  const [duplicates, setDuplicates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [resolutions, setResolutions] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredDuplicates, setFilteredDuplicates] = useState([]);
  const [paginatedDuplicates, setPaginatedDuplicates] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedRecords, setExpandedRecords] = useState({});
  const [summaryFields, setSummaryFields] = useState(['title', 'isbn', 'format', 'creators']);
  const [editableFields, setEditableFields] = useState(DEFAULT_EDITABLE_FIELDS);
  const [duplicateCheckFields, setDuplicateCheckFields] = useState(DEFAULT_DUPLICATE_CHECK_FIELDS); // New state

  // Refs to prevent re-renders
  const isMountedRef = useRef(true);
  const importSessionIdRef = useRef(importSession.id);

  // Get API functions
  const { detectDuplicates, resolveDuplicates, getDuplicateFields } = useCsvImport();

  // Load duplicates and entity-specific fields on mount
  useEffect(() => {
    const fetchData = async () => {
      if (!isMountedRef.current) return;

      try {
        setIsLoading(true);

        // Fetch entity-specific fields first
        try {
          const fieldsData = await getDuplicateFields(importSessionIdRef.current);
          if (fieldsData && fieldsData.summary_fields && fieldsData.summary_fields.length > 0) {
            setSummaryFields(fieldsData.summary_fields);
          }
          if (fieldsData && fieldsData.editable_fields && fieldsData.editable_fields.length > 0) {
            setEditableFields(fieldsData.editable_fields);
          }
          // Fetch and set duplicate check fields
          if (fieldsData && fieldsData.duplicate_check_fields && fieldsData.duplicate_check_fields.length > 0) {
            setDuplicateCheckFields(fieldsData.duplicate_check_fields);
          } else {
            console.warn('API did not provide duplicate_check_fields, using defaults.');
            setDuplicateCheckFields(DEFAULT_DUPLICATE_CHECK_FIELDS); // Use default if not provided
          }
        } catch (error) {
          console.warn('Could not fetch entity-specific fields, using defaults', error);
          // Ensure defaults are set even if API call fails entirely
          setDuplicateCheckFields(DEFAULT_DUPLICATE_CHECK_FIELDS);
        }

        // Then fetch duplicates
        const data = await detectDuplicates(importSessionIdRef.current);

        if (!isMountedRef.current) return;

        setDuplicates(data);

        // Initialize resolutions with default 'skip' action
        const initialResolutions = {};
        data.forEach(duplicate => {
          initialResolutions[duplicate.id] = 'skip';
        });
        setResolutions(initialResolutions);
      } catch (error) {
        if (isMountedRef.current) {
          onError(error.message);
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    // Cleanup function
    return () => {
      isMountedRef.current = false;
    };
  }, []); // Empty dependency array - only run once on mount

  // Filter duplicates when search query or duplicates change
  useEffect(() => {
    if (!isMountedRef.current) return;

    const filterDuplicates = () => {
      const searchLower = searchQuery.toLowerCase();

      const filtered = duplicates.filter(duplicate => {
        // Parse JSON strings if they're not already objects
        let csvRecord = duplicate.csv_record_data;
        let existingRecord = duplicate.existing_record_data;

        try {
          if (typeof csvRecord === 'string') {
            csvRecord = JSON.parse(csvRecord);
          }

          if (typeof existingRecord === 'string') {
            existingRecord = JSON.parse(existingRecord);
          }
        } catch (error) {
          console.error('Error parsing record data:', error);
          return false;
        }

        // Convert objects to arrays of {field, value} for searching
        const csvFields = csvRecord ? Object.entries(csvRecord).map(([field, value]) => ({
          field,
          value: String(value || '')
        })) : [];

        const existingFields = existingRecord ? Object.entries(existingRecord).map(([field, value]) => ({
          field,
          value: String(value || '')
        })) : [];

        // Search in the arrays
        return (
          csvFields.some(field =>
            field.value.toLowerCase().includes(searchLower)
          ) ||
          existingFields.some(field =>
            field.value.toLowerCase().includes(searchLower)
          )
        );
      });

      setFilteredDuplicates(filtered);
    };

    filterDuplicates();
  }, [duplicates, searchQuery]);

  // Update pagination when filtered duplicates change
  useEffect(() => {
    if (!isMountedRef.current) return;

    const updatePagination = () => {
      const total = Math.ceil(filteredDuplicates.length / ITEMS_PER_PAGE);
      setTotalPages(total || 1);

      const paginated = filteredDuplicates.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
      );

      setPaginatedDuplicates(paginated);
    };

    updatePagination();
  }, [filteredDuplicates, currentPage]);

  // Handle resolution change
  const handleResolutionChange = useCallback((duplicateId, resolution) => {
    setResolutions(prev => ({
      ...prev,
      [duplicateId]: resolution
    }));
  }, []);

  // Handle save resolutions
  const handleSaveResolutions = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      setIsSaving(true);
      await resolveDuplicates(importSessionIdRef.current, resolutions);

      if (isMountedRef.current && onComplete) {
        onComplete();
      }
    } catch (error) {
      if (isMountedRef.current && onError) {
        onError(error.message);
      }
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [resolutions, resolveDuplicates, onComplete, onError]);

  // Toggle expanded state for a record
  const toggleExpandRecord = useCallback((duplicateId) => {
    setExpandedRecords(prev => ({
      ...prev,
      [duplicateId]: !prev[duplicateId]
    }));
  }, []);

  // Helper function to extract creator information
  const extractCreatorInfo = useCallback((record) => {
    // If we already have creators_display, use it
    if (record && record.creators_display) {
      return record.creators_display;
    }

    // Try to extract from creators array if it exists
    if (record && Array.isArray(record.creators)) {
      try {
        const creatorNames = record.creators
          .map(creator => {
            if (typeof creator === 'string') return creator;
            if (creator.display_name) return creator.display_name;
            if (creator.name) return creator.name;
            return String(creator);
          })
          .filter(Boolean);

        if (creatorNames.length > 0) {
          return creatorNames.join('; ');
        }
      } catch (e) {
        console.error('Error extracting from creators array:', e);
      }
    }

    // Try to extract from productcreator_set if it exists
    if (record && Array.isArray(record.productcreator_set)) {
      try {
        const creatorNames = record.productcreator_set
          .map(pc => {
            if (!pc.creator) return '';
            if (typeof pc.creator === 'string') return pc.creator;
            if (pc.creator.display_name) return pc.creator.display_name;
            if (pc.creator.name) return pc.creator.name;
            return String(pc.creator);
          })
          .filter(Boolean);

        if (creatorNames.length > 0) {
          return creatorNames.join('; ');
        }
      } catch (e) {
        console.error('Error extracting from productcreator_set:', e);
      }
    }

    return '';
  }, []);

  // Render functions
  const renderTableRow = useCallback((field, csvValue, existingValue, existingRecord) => {
    // Check if we have a display field for this field in the existingRecord
    const displayField = `${field}_display`;
    let displayValue = existingValue;

    // If we have a display field in the existingRecord, use that instead
    if (existingRecord && existingRecord[displayField]) {
      displayValue = existingRecord[displayField];
    }

    // Special handling for creators field
    if (field === 'creators') {
      // Log for debugging
      console.log('Rendering creators field:', { csvValue, existingValue, existingRecord });

      // Use our helper function to extract creator information
      displayValue = extractCreatorInfo(existingRecord);
      console.log('Extracted creator info:', displayValue);
    }

    // Special handling for format field
    if (field === 'format' && existingRecord && existingRecord['format_display']) {
      displayValue = existingRecord['format_display'];
    }

    return (
      <Table.Row key={field}>
        <Table.TextCell>
          <Strong>{field}</Strong>
        </Table.TextCell>
        <Table.TextCell>
          {csvValue !== undefined ? String(csvValue) : ''}
        </Table.TextCell>
        <Table.TextCell>
          {displayValue !== undefined ? String(displayValue) : ''}
        </Table.TextCell>
      </Table.Row>
    );
  }, [extractCreatorInfo]);

  const renderDuplicateCard = useCallback((duplicate) => {
    // Parse JSON strings if they're not already objects
    let csvRecord = duplicate.csv_record_data;
    let existingRecord = duplicate.existing_record_data;

    try {
      if (typeof csvRecord === 'string') {
        csvRecord = JSON.parse(csvRecord);
      }

      if (typeof existingRecord === 'string') {
        existingRecord = JSON.parse(existingRecord);
      }

      // Debug logging for creators
      console.log('Duplicate ID:', duplicate.id);
      console.log('CSV Record:', csvRecord);
      console.log('Existing Record:', existingRecord);

      // Check if we have creators_display in the existing record
      if (existingRecord.creators_display) {
        console.log('Found creators_display:', existingRecord.creators_display);
      } else {
        console.warn('No creators_display found in existing record');
      }

    } catch (error) {
      console.error('Error parsing record data:', error);
      return (
        <Card
          key={duplicate.id}
          backgroundColor="tint1"
          padding={majorScale(2)}
          marginBottom={majorScale(2)}
        >
          <Text>Error parsing record data</Text>
        </Card>
      );
    }

    const isExpanded = expandedRecords[duplicate.id] || false;

    // Get summary fields for the summary view
    const summaryFieldsToShow = summaryFields.filter(field =>
      csvRecord[field] !== undefined || existingRecord[field] !== undefined
    );

    // Get editable fields for the expanded view
    const editableFieldsToShow = editableFields.filter(field =>
      csvRecord[field] !== undefined || existingRecord[field] !== undefined
    );

    return (
      <Card
        key={duplicate.id}
        backgroundColor="tint1"
        padding={majorScale(2)}
        marginBottom={majorScale(2)}
        borderBottom="4px solid var(--maroon)"
      >
        <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={majorScale(2)}>
          <Strong>Record {duplicate.row_index + 1}</Strong>
          <IconButton
            icon={isExpanded ? ChevronUpIcon : ChevronDownIcon}
            appearance='large'
            title="View all editable fields"
            size={122}
            onClick={() => toggleExpandRecord(duplicate.id)}
          />
        </Pane>

        {/* Display the reason for the duplicate flag */}
        {duplicateCheckFields.length > 0 && (
          <Alert
            intent="warning"
            title="Duplicate Reason"
            marginBottom={majorScale(1)}
            paddingY={majorScale(1)} // Adjust vertical padding
            paddingX={majorScale(2)} // Adjust horizontal padding
          >
            Potential duplicate detected based on matching field(s): <Strong>{duplicateCheckFields.join(', ')}</Strong>.
          </Alert>
        )}

        {/* Summary view (always visible) */}
        <Table marginBottom={majorScale(2)}>
          <Table.Head>
            <Table.TextHeaderCell>Field</Table.TextHeaderCell>
            <Table.TextHeaderCell>CSV Value</Table.TextHeaderCell>
            <Table.TextHeaderCell>Existing Value</Table.TextHeaderCell>
          </Table.Head>
          <Table.Body>
            {summaryFieldsToShow.map(field => renderTableRow(
              field,
              csvRecord[field],
              existingRecord[field],
              existingRecord
            ))}
          </Table.Body>
        </Table>

        {/* Expanded view (only visible when expanded) */}
        {isExpanded && (
          <Pane marginBottom={majorScale(2)}>
            <Heading size={400} marginBottom={majorScale(1)}>Updateable Fields</Heading>
            <Table marginBottom={majorScale(2)}>
              <Table.Head>
                <Table.TextHeaderCell>Field</Table.TextHeaderCell>
                <Table.TextHeaderCell>CSV Value</Table.TextHeaderCell>
                <Table.TextHeaderCell>Existing Value</Table.TextHeaderCell>
              </Table.Head>
              <Table.Body>
                {editableFieldsToShow.map(field => renderTableRow(
                  field,
                  csvRecord[field],
                  existingRecord[field],
                  existingRecord
                ))}
              </Table.Body>
            </Table>
          </Pane>
        )}

        <Pane>
          <Strong marginBottom={majorScale(1)}>Resolution:</Strong>
          <Pane display="flex" gap={majorScale(3)}>
            <Radio
              name={`resolution-${duplicate.id}`}
              label="Skip Import"
              checked={resolutions[duplicate.id] === 'skip'}
              onChange={() => handleResolutionChange(duplicate.id, 'skip')}
            />
            <Radio
              name={`resolution-${duplicate.id}`}
              label="Update Existing"
              checked={resolutions[duplicate.id] === 'update'}
              onChange={() => handleResolutionChange(duplicate.id, 'update')}
            />
            <Radio
              name={`resolution-${duplicate.id}`}
              label="Create New"
              checked={resolutions[duplicate.id] === 'create'}
              onChange={() => handleResolutionChange(duplicate.id, 'create')}
            />
          </Pane>
        </Pane>
      </Card>
    );
  }, [expandedRecords, summaryFields, editableFields, resolutions, handleResolutionChange, toggleExpandRecord, renderTableRow, duplicateCheckFields]);

  // Render loading state
  if (isLoading) {
    return (
      <Pane display="flex" alignItems="center" justifyContent="center" height={400}>
        <Spinner />
      </Pane>
    );
  }

  // Render when no duplicates are found
  if (!isLoading && duplicates.length === 0) {
    return (
      <Pane>
        <Alert
          intent="success"
          title="No Duplicates Found"
          marginBottom={majorScale(2)}
        >
          No potential duplicates were detected in your data based on the configured unique fields.
        </Alert>
        <Pane display="flex" justifyContent="flex-end">
          <Button
            appearance="primary"
            onClick={onComplete}
          >
            Continue Import
          </Button>
        </Pane>
      </Pane>
    );
  }

  return (
    <Pane>
      <Heading size={600} marginBottom={majorScale(2)}>
        Resolve Duplicates
      </Heading>
      <Text marginBottom={majorScale(2)}>
        The following records match existing items in the database. Please decide how to handle each duplicate.
      </Text>

      {/* Search and pagination controls */}
      <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={majorScale(2)}>
        <SearchInput
          placeholder="Search duplicates..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          width={300}
        />
        <Pagination
          page={currentPage}
          totalPages={totalPages}
          onPageChange={page => setCurrentPage(page)}
        />
      </Pane>

      {/* Duplicate cards */}
      {paginatedDuplicates.map(duplicate => renderDuplicateCard(duplicate))}

      {/* Pagination and action buttons */}
      <Pane display="flex" justifyContent="flex-end" marginTop={majorScale(3)}>
        <Button
          appearance="primary"
          onClick={handleSaveResolutions}
          isLoading={isSaving}
          disabled={isSaving}
        >
          Save Resolutions & Continue
        </Button>
      </Pane>
    </Pane>
  );
};

export default DuplicateResolver;
